// Conteúdo das REGIÕES — nem núcleo (não é UI genérica) nem AVA (não é payload de
// atividade). É a arte e os textos de cada bioma, mais o catálogo que resolve
// `trailRegion` → conteúdo.
//
// A REGIÃO GEOGRÁFICA É A REGIÃO DO BIOMA, e há um nome só: `norte` **é** a
// Amazônia como `nordeste` **é** a Caatinga. "Amazônia" e "Caatinga" aparecem
// apenas como `bioma`, que é rótulo para o aluno ler. O id da região é ao mesmo
// tempo:
//
//   - a chave em `activity_ludic_trail_settings.region`, que o professor escolhe
//     ao criar a trilha;
//   - o PREFIXO dos `card_id` (`nordeste-problema-01`), que são a chave da coleção
//     do aluno e não se renomeiam depois de gravados;
//   - o `#regiao-<id>` do SVG do mapa da abertura;
//   - o valor de `data-tl-region` no CSS.
//
// Um id por coisa é decisão de 05/08/2026, e a razão é o preço do contrário: dois
// nomes para a mesma coisa é o defeito de `isTrail` / `isTrailView`, que já custou
// uma seção inteira de README para explicar.
//
// ACRESCENTAR UMA REGIÃO: uma pasta em `regioes/<id>/`, o descritor lá dentro (ver
// `contrato.js` para o que ele precisa ter) e uma linha em `REGIOES` aqui. Nada
// mais no módulo — nem CSS, nem núcleo, nem `ava/`.

import { REGIAO_CENTRO_OESTE } from "./regioes/centro-oeste";
import { REGIAO_NORDESTE } from "./regioes/nordeste";
import { REGIAO_NORTE } from "./regioes/norte";
import { REGIAO_SUDESTE } from "./regioes/sudeste";
import { REGIAO_SUL } from "./regioes/sul";

// Import ESTÁTICO, e não `import()` dinâmico, embora o plano previsse o contrário.
// A medição desfez o problema: o AVA é `react-scripts@4.0.3`, cujo `url-loader`
// tem `imageInlineSizeLimit` de 10000 bytes, e as cartas derivadas têm 45-67 KB.
// Cada uma é emitida como ARQUIVO SEPARADO e só é buscada quando o `<img>`
// renderiza — o que um import estático custa no bundle é uma string de URL. Cinco
// regiões seriam 110 strings e ZERO byte de imagem.
//
// E não fazer o split preserva algo que vale: `ava/TrilhaDeAtividade` calcula o
// sorteio num inicializador de `useState`, síncrono de propósito (se o avatar
// chegasse depois da primeira renderização, o aluno veria o onboarding piscar
// antes de o tabuleiro aparecer). Com região assíncrona isso viraria estado de
// carregamento.
// A ORDEM aqui é a que o professor vê no campo "Região da trilha" (ver
// `regioesDisponiveis`). Nordeste primeiro por ser a que existe desde julho e a
// padrão; depois as demais, na ordem em que a arte chegar.
export const REGIOES = {
  nordeste: REGIAO_NORDESTE,
  norte: REGIAO_NORTE,
  "centro-oeste": REGIAO_CENTRO_OESTE,
  sudeste: REGIAO_SUDESTE,
  sul: REGIAO_SUL
};

// A região de quem não pediu nenhuma, e o fallback de `trailRegion` desconhecida.
// É a mesma que `LudicTrailSetting::REGIAO_PADRAO` devolve no backend para
// atividade sem registro — as duas pontas precisam concordar, senão uma trilha
// criada antes da tabela existir mostraria uma região no servidor e outra na tela.
export const REGIAO_PADRAO = REGIAO_NORDESTE;

/**
 * A região de um id, com o padrão aplicado.
 *
 * O fallback não é cortesia: `trailRegion` vem do banco, onde a coluna aceita
 * qualquer string de 32 caracteres, e região desconhecida sem fallback quebraria a
 * cerimônia de card e o encerramento — telas em que o aluno já está no meio da
 * atividade.
 *
 * @param {string} [id] `trailRegion` do payload da atividade
 */
export function getRegiao(id) {
  return (id && REGIOES[id]) || REGIAO_PADRAO;
}

/**
 * O catálogo, para quem precisa OFERECER as regiões em vez de usar uma.
 *
 * É o que alimenta o campo "Região da trilha" do `ActivityCreator`. Devolvia só
 * `{ id, nome, bioma }`, que era o que um `<select>` precisava; desde 05/08/2026 a
 * escolha é VISUAL — um cartão por região, com o mascote e a cor dela —, então
 * vai junto o mínimo para desenhar isso.
 *
 * O que vai, e por que só isto:
 *
 * - `mascote.nome` e `mascote.cabeca`: a CABEÇA, e não o corpo. É o enquadramento
 *   fechado (320x399, contra 300x374 do corpo — mais pixels, não menos), e é o
 *   mesmo asset do medalhão do nó de recompensa. Quem o recorta em círculo precisa
 *   de `object-position: top`: centrado, o quadrado corta as orelhas da onça.
 * - o degradê `fundo*`: o que distingue Caatinga de Amazônia de longe, antes de o
 *   professor ler o nome. A arte da cabeça é RGBA transparente de propósito (ver
 *   o aviso sobre disco branco no README), então quem a mostra precisa pintar o
 *   fundo.
 * - `cores.chrome*`, e NÃO `region*`: o cartão do professor fica sobre BRANCO, que
 *   é exatamente a situação para a qual o cromo foi escolhido. No Norte o accent
 *   `region` é o ouro da onça — quente de propósito, porque na experiência do
 *   aluno ele aparece sobre o degradê verde da casca; sobre branco, ele lê como
 *   dourado e não como Amazônia. O cromo do Norte é verde-floresta (`#4f9d3a` /
 *   `#2f6b3a`) e o da Caatinga é âmbar, cada um já pensado para fundo claro.
 *
 * NÃO vai o descritor inteiro: cartas, perfil da estrada e tabuleiro são para
 * quem RENDERIZA a trilha, e quem só oferece a escolha não deve carregá-los na
 * assinatura. Para o descritor completo existe `getRegiao`.
 */
export function regioesDisponiveis() {
  return Object.values(REGIOES).map(({ id, nome, bioma, mascote, cores }) => ({
    id,
    nome,
    bioma,
    mascote: {
      nome: mascote.nome,
      cabeca: mascote.cabeca
    },
    cores: {
      fundoTopo: cores.fundoTopo,
      fundoMeio: cores.fundoMeio,
      fundoBase: cores.fundoBase,
      chromeBorda: cores.chromeBorda,
      chromeTexto: cores.chromeTexto,
      chromeFundo: cores.chromeFundo
    }
  }));
}

/**
 * O ACERVO INTEIRO, região por região — para quem mostra a COLEÇÃO do aluno.
 *
 * A diferença em relação a `regioesDisponiveis` é o que está na assinatura e o
 * motivo de existirem as duas: lá o professor ESCOLHE uma região e as cartas não
 * interessam; aqui o aluno vê o que tem e, principalmente, **o que falta** — então
 * as cartas são o dado principal, e vão todas, conquistadas ou não. Quem cruza com
 * a coleção dele é `nucleo/colecao.js`.
 *
 * AS 22 DE CADA REGIÃO NUMA LISTA SÓ, comuns antes das raras. Duas consequências
 * que valem:
 *
 * - a ordem é a do CATÁLOGO, e não a da conquista. É o que dá "lugares" fixos ao
 *   álbum: a carta que falta fica sempre no mesmo buraco, e o aluno reconhece o
 *   buraco de uma visita para a outra. Ordenar por data de conquista faria a grade
 *   se rearranjar a cada carta nova, que é o oposto de colecionar;
 * - as raras no FIM porque é onde o olho chega por último, e elas são o prêmio —
 *   quem abre a coleção vê primeiro a fileira do dia a dia.
 *
 * `cores` traz mais que `regioesDisponiveis`: vão também `region`/`regionStrong`,
 * que lá são omitidos de propósito porque o cartão do professor fica sobre BRANCO.
 * Aqui o painel de cada região é pintado com o degradê dela (`fundo*`), que é a
 * mesma vizinhança da trilha — e é ali que o accent foi escolhido para funcionar.
 *
 * E VAI UM CAMPO DE `tabuleiro`, UM SÓ: `fundoColecao`, a cena da faixa de horizonte
 * do painel. A projeção continua campo a campo, como a de `cores`, e não passa a ser
 * `tabuleiro` inteiro — o tile, o perfil da estrada e a proporção são do tabuleiro, e
 * a coleção não desenha tabuleiro nenhum. Repassar o objeto todo seria dar ao álbum
 * acesso a dado que ele não usa, e é assim que uma tela passa a depender de algo por
 * acidente.
 *
 * Este campo é a razão pela qual a faixa não aparecia quando o CSS dela foi escrito:
 * `variaveisDaRegiao` lê `tabuleiro.fundoColecao`, e aqui `tabuleiro` não existia. A
 * variável saía vazia, o `background` caía no `none` do fallback, e o painel ficava
 * exatamente como antes — sem erro em lugar nenhum.
 */
export function acervoDeCartas() {
  return Object.values(REGIOES).map(regiao => {
    const { id, nome, bioma, mascote, cores, tabuleiro } = regiao;
    return {
      id,
      nome,
      bioma,
      mascote: {
        nome: mascote.nome,
        cabeca: mascote.cabeca
      },
      cores: {
        region: cores.region,
        regionStrong: cores.regionStrong,
        fundoTopo: cores.fundoTopo,
        fundoMeio: cores.fundoMeio,
        fundoBase: cores.fundoBase,
        chromeBorda: cores.chromeBorda,
        chromeTexto: cores.chromeTexto,
        chromeFundo: cores.chromeFundo
      },
      tabuleiro: { fundoColecao: tabuleiro && tabuleiro.fundoColecao },
      cartas: [...(regiao.cardsComuns || []), ...(regiao.cardsRaros || [])]
    };
  });
}

/**
 * Um card pelo id, em QUALQUER região.
 *
 * Varre o catálogo inteiro de propósito: a coleção do aluno atravessa regiões (é o
 * acervo dele, não o álbum de uma trilha), então quem chama isto pode ter em mão o
 * id de uma carta de região que não é a da trilha aberta.
 */
export function getCardPorId(id) {
  if (!id) return null;

  const regioes = Object.values(REGIOES);
  for (let i = 0; i < regioes.length; i += 1) {
    const { cardsComuns = [], cardsRaros = [] } = regioes[i];
    const achado =
      cardsComuns.find(card => card.id === id) ||
      cardsRaros.find(card => card.id === id);
    if (achado) return achado;
  }
  return null;
}

export { AVATARES, getAvatar, AVATAR_PADRAO } from "./avatares";
export { problemasDaRegiao, validarRegiao } from "./contrato";
